import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import StatusIndicator from '../../../components/ui/StatusIndicator';

const VendorTable = ({
  vendors = [],
  selectedVendors = [],
  onSelectionChange,
  onEditVendor,
  onDeleteVendor,
  onViewMatchingRules,
  sortConfig,
  onSort
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(vendors?.map(vendor => vendor?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectVendor = (vendorId, checked) => {
    if (checked) {
      onSelectionChange([...selectedVendors, vendorId]);
    } else {
      onSelectionChange(selectedVendors?.filter(id => id !== vendorId));
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getMatchingRuleStatus = (rulesCount) => {
    if (rulesCount === 0) return 'none';
    if (rulesCount <= 2) return 'few';
    return 'many';
  };

  const getMatchingRuleStatusConfig = (status) => {
    switch (status) {
      case 'none':
        return { color: 'text-muted-foreground', text: 'No rules' };
      case 'few':
        return { color: 'text-warning', text: 'Few rules' };
      case 'many':
        return { color: 'text-success', text: 'Active rules' };
      default:
        return { color: 'text-muted-foreground', text: 'Unknown' };
    }
  };

  if (vendors?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg">
        <div className="p-12 text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Users" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No vendors found</h3>
          <p className="text-muted-foreground mb-6">
            Get started by importing vendors from Sage 50 or adding them manually.
          </p>
          <div className="flex items-center justify-center space-x-3">
            <Button variant="default" iconName="Upload" iconPosition="left">
              Import CSV
            </Button>
            <Button variant="outline" iconName="Plus" iconPosition="left">
              Add Vendor
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={selectedVendors?.length === vendors?.length}
                  indeterminate={selectedVendors?.length > 0 && selectedVendors?.length < vendors?.length}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('companyName')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Company Name</span>
                  <Icon name={getSortIcon('companyName')} size={14} />
                </button>
              </th>
              
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('contactName')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Contact</span>
                  <Icon name={getSortIcon('contactName')} size={14} />
                </button>
              </th>
              
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('accountReference')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Account Ref</span>
                  <Icon name={getSortIcon('accountReference')} size={14} />
                </button>
              </th>
              
              <th className="text-left px-4 py-3">
                <span className="text-sm font-medium text-foreground">VAT Number</span>
              </th>
              
              <th className="text-left px-4 py-3">
                <span className="text-sm font-medium text-foreground">Matching Rules</span>
              </th>
              
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('lastUpdated')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Last Updated</span>
                  <Icon name={getSortIcon('lastUpdated')} size={14} />
                </button>
              </th>
              
              <th className="w-24 px-4 py-3">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          
          <tbody>
            {vendors?.map((vendor) => {
              const isSelected = selectedVendors?.includes(vendor?.id);
              const isHovered = hoveredRow === vendor?.id;
              const matchingRuleStatus = getMatchingRuleStatus(vendor?.matchingRulesCount);
              const statusConfig = getMatchingRuleStatusConfig(matchingRuleStatus);
              
              return (
                <tr
                  key={vendor?.id}
                  className={`
                    border-b border-border transition-smooth
                    ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/30'}
                  `}
                  onMouseEnter={() => setHoveredRow(vendor?.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td className="px-4 py-4">
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => handleSelectVendor(vendor?.id, e?.target?.checked)}
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground">{vendor?.companyName}</span>
                      {vendor?.tradingName && vendor?.tradingName !== vendor?.companyName && (
                        <span className="text-sm text-muted-foreground">
                          Trading as: {vendor?.tradingName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-foreground">{vendor?.contactName}</span>
                      <span className="text-xs text-muted-foreground">{vendor?.email}</span>
                      {vendor?.phone && (
                        <span className="text-xs text-muted-foreground">{vendor?.phone}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="font-mono text-sm text-foreground">{vendor?.accountReference}</span>
                  </td>
                  <td className="px-4 py-4">
                    {vendor?.vatNumber ? (
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-sm text-foreground">{vendor?.vatNumber}</span>
                        <StatusIndicator
                          status={vendor?.vatVerified ? 'reviewed' : 'needs-review'}
                          variant="dot"
                          size="sm"
                          showText={false}
                          confidence={vendor?.vatVerified ? 'high' : 'low'}
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Not provided</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => onViewMatchingRules(vendor?.id)}
                      className={`
                        flex items-center space-x-2 text-sm transition-smooth
                        ${statusConfig?.color} hover:text-primary
                      `}
                    >
                      <Icon name="Link" size={14} />
                      <span>{vendor?.matchingRulesCount} rules</span>
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-muted-foreground">
                      {formatDate(vendor?.lastUpdated)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEditVendor(vendor?.id)}
                        className="h-8 w-8"
                      >
                        <Icon name="Edit" size={14} />
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDeleteVendor(vendor?.id)}
                        className="h-8 w-8 text-error hover:text-error hover:bg-error/10"
                      >
                        <Icon name="Trash2" size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VendorTable;